import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Label,
	RadioGroup,
	RadioGroupItem,
} from "@dune/ui";
import {
	WEBHOOK_EVENT_DETAILS,
	WEBHOOK_EVENT_VALUES,
	type WebhookEvent,
} from "@dune/types";
import { useEffect, useState, type FormEvent } from "react";

export interface TriggerTestEventDialogProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (event: WebhookEvent) => Promise<void> | void;
	isSubmitting: boolean;
}

export const TriggerTestEventDialog = ({
	open,
	onClose,
	onSubmit,
	isSubmitting,
}: TriggerTestEventDialogProps) => {
	const [selectedEvent, setSelectedEvent] = useState<WebhookEvent>(
		WEBHOOK_EVENT_VALUES[0],
	);

	useEffect(() => {
		if (open) {
			setSelectedEvent(WEBHOOK_EVENT_VALUES[0]);
		}
	}, [open]);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		await onSubmit(selectedEvent);
	};

	return (
		<Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
			<DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Envoyer un event de test</DialogTitle>
					<DialogDescription>
						Choisissez un évènement, nous générons automatiquement un payload
						représentatif pour vos webhooks.
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-6">
					<div>
						<p className="text-sm font-medium">Évènement a simuler</p>
						<RadioGroup
							value={selectedEvent}
							onValueChange={(value) => setSelectedEvent(value as WebhookEvent)}
							className="mt-3"
						>
							{WEBHOOK_EVENT_VALUES.map((event) => (
								<Label
									key={`event-${event}`}
									htmlFor={`trigger-event-${event}`}
									className="flex cursor-pointer items-start gap-3 rounded-md border p-3 shadow-sm transition-colors hover:bg-accent has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
								>
									<div>
										<RadioGroupItem
											id={`trigger-event-${event}`}
											value={event}
										/>
									</div>

									<p className="font-medium">
										{WEBHOOK_EVENT_DETAILS[event].label}
									</p>
									<p className="text-sm text-muted-foreground">
										{WEBHOOK_EVENT_DETAILS[event].description}
									</p>
								</Label>
							))}
						</RadioGroup>
					</div>

					<div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
						Le backend génère un payload de test approprié a l'évènement
						séléctionné.
					</div>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={onClose}>
							Annuler
						</Button>
						<Button type="submit" disabled={isSubmitting}>
							{isSubmitting ? "Envoi en cours..." : "Envoyer"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
